import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, AlertTriangle, Clock, 
  CheckCircle2, Shield, BarChart3, Users, ArrowRight,
  AlertCircle, ThermometerSun, Scale, FileWarning, Package, FileText, Settings, ClipboardCheck, FileDown, Loader2, Circle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useApp } from '../context/AppContext';
import { useTrace } from '../context/TraceContext';
import { useFilters } from '../context/FilterContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  formatIndianCurrency, 
  getMetricsByFilter,
  getAtRiskItems,
  getComplianceTrend,
  getDeviationsByCategory,
  plants
} from '../data/mockData';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const COLORS = ['#fd4816', '#3b82f6', '#10b981', '#f59e0b'];

// Icon mapping for at-risk items
const iconMap = {
  ThermometerSun,
  Scale,
  FileWarning,
  Package,
  FileText,
  AlertTriangle,
  Settings,
  ClipboardCheck,
  Users
};

const demoPaths = [
  { title: 'Run Daily GMP Checklist → Create Deviation', path: '/tasks', description: 'Execute checklist in iPad Mode, fail an item to auto-create deviation' },
  { title: 'Investigate Deviation → CAPA → Close', path: '/deviations', description: 'Full investigation workflow with root cause and corrective actions' },
  { title: 'Create Change Control → Approvals', path: '/change-control', description: 'Risk-based change request with multi-level approval' },
  { title: 'Run Self-Inspection → Finding → CAPA', path: '/audits/self-inspections', description: 'Annual self-inspection workflow per Schedule-M' },
  { title: 'Generate Audit Pack → Auditor View', path: '/audit-packs', description: 'Create compliance evidence pack with integrity verification' }
];

const Dashboard = () => {
  const { currentRole, isReadOnly } = useApp();
  const { pidTraceMode } = useTrace();
  const { plant, area, timeRange } = useFilters();
  const [isGeneratingCXO, setIsGeneratingCXO] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  // Get filtered data based on current filter selections
  const metrics = useMemo(() => getMetricsByFilter(plant, area, timeRange), [plant, area, timeRange]);
  const atRiskItems = useMemo(() => getAtRiskItems(plant, area), [plant, area]);
  const complianceTrendData = useMemo(() => getComplianceTrend(plant, area, timeRange), [plant, area, timeRange]);
  const deviationsByCategory = useMemo(() => getDeviationsByCategory(plant, area), [plant, area]);

  // Get current plant name
  const currentPlantName = plants.find(p => p.id === plant)?.name || 'Plant A';

  const handleGenerateCXOReport = async () => {
    setIsGeneratingCXO(true);
    
    const progressMessages = [
      'Reading dashboard data...',
      'Checking compliance metrics...',
      'Analyzing risk factors...',
      'Compiling executive summary...',
      'Opening report...'
    ];

    // Show progress messages with delays
    for (let i = 0; i < progressMessages.length; i++) {
      setProgressMessage(progressMessages[i]);
      await new Promise(resolve => setTimeout(resolve, i === progressMessages.length - 1 ? 1000 : 800));
    }

    try {
      // Open the SharePoint link in a new tab
      const reportUrl = 'https://myswishclub-my.sharepoint.com/:b:/g/personal/gourab_paul_swishclub_in/IQBIIxdSQE-NQ4gC9pcSyWgYAe1O4X23VXg52wKTPbZPPH8?e=lff3qf';
      window.open(reportUrl, '_blank', 'noopener,noreferrer');

      setIsGeneratingCXO(false);
      setProgressMessage('');
    } catch (error) {
      console.error('Error opening CXO report:', error);
      setIsGeneratingCXO(false);
      setProgressMessage('');
      alert('Failed to open report. Please try again.');
    }
  };

  const KPICard = ({ title, value, unit, trend, trendValue, icon: Icon, link, color, prd, req }) => (
    <Link to={link}>
      <Card className={`bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden ${pidTraceMode ? 'border-cyan-300' : ''}`}>
        <TraceLabel prd={prd} req={req} />
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">{title}</p>
              <div className="flex items-baseline gap-1">
                <span className={`text-2xl font-bold ${color ? '' : 'text-slate-900'}`} style={{ color: color }}>{value}</span>
                {unit && <span className="text-sm text-slate-500">{unit}</span>}
              </div>
              {trend && (
                <div className={`flex items-center gap-1 mt-1 text-xs ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {trend === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  <span>{trendValue}</span>
                </div>
              )}
            </div>
            {Icon && (
              <div className={`p-2 rounded-md bg-opacity-10`} style={{ backgroundColor: color ? `${color}15` : '#f1f5f9' }}>
                <Icon className="h-5 w-5" style={{ color: color || '#64748b' }} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-shrink-0 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Compliance Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {currentPlantName} • Operating Standard: Schedule-M (India GMP) + WHO-GMP aligned
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 lg:ml-auto -mr-6 pr-6 w-full sm:w-auto">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto">
            <a
              href="https://myswishclub-my.sharepoint.com/:b:/g/personal/gourab_paul_swishx_com1/IQB0hCOy6WK3QZa8AuCP4yp6Ad0wILzRSzkyY9VusaIWgGY?e=g0xhDj"
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <Button
                className="bg-[#fd4816] hover:bg-[#fd4816]/90 text-white relative overflow-visible text-xs sm:text-sm md:text-base px-3 sm:px-4 py-2 w-full sm:w-auto flex items-center justify-center"
              >
                <FileDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                <span className="flex-1 text-center sm:text-left text-[10px] sm:text-xs md:text-sm leading-tight">Download Your Compliance Readiness Report</span>
                <span className="ml-1.5 sm:ml-2 relative inline-flex items-center flex-shrink-0">
                  <Circle className="h-3 w-3 sm:h-4 sm:w-4 fill-white text-white cxo-glow-icon" />
                  <span className="absolute inset-0 rounded-full bg-white cxo-attention-badge opacity-75"></span>
                </span>
              </Button>
            </a>
            {/* <ReportExportButton
              title="Compliance Dashboard"
              subtitle={`${currentPlantName} • Schedule-M + WHO-GMP aligned`}
            /> */}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
              WHO-GMP Certified (Sample): WHOGMP/sQMS/2025-A
            </Badge>
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">
              Valid until 31 Mar 2027
            </Badge>
            {isReadOnly && (
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">
                READ-ONLY VIEW
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* CXO Report Generation Dialog */}
      <Dialog open={isGeneratingCXO} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Generating CXO Compliance Readiness Report</DialogTitle>
            <DialogDescription>
              Please wait while we compile your compliance report...
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#fd4816] mb-4" />
            <p className="text-sm text-slate-600 font-medium">{progressMessage}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI Cards */}
      <ReportSection
        id="kpis"
        title="Key Performance Indicators"
        description="Schedule-M & WHO-GMP readiness metrics"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
        <KPICard
          title="Schedule-M Compliance"
          value={metrics.complianceScore}
          unit="%"
          trend="up"
          trendValue="+2% vs last period"
          icon={Shield}
          link="/requirements"
          color="#10b981"
          prd="PRD §3"
          req="SM-PQS-001"
        />
        <KPICard
          title="Overdue Critical Tasks"
          value={metrics.overdueCriticalTasks}
          icon={Clock}
          link="/tasks?filter=overdue&criticality=critical"
          color="#ef4444"
          prd="PRD §6.3"
          req="SM-DI-001"
        />
        <KPICard
          title="Open Deviations"
          value={metrics.openDeviations}
          trend="down"
          trendValue="-3 vs last period"
          icon={AlertTriangle}
          link="/deviations?status=open"
          color="#f59e0b"
          prd="PRD §6.4"
          req="SM-DEV-001"
        />
        <KPICard
          title="CAPA Aging (30+ days)"
          value={metrics.capaAging30Plus}
          icon={AlertCircle}
          link="/capa?aging=30"
          color="#ef4444"
          prd="PRD §6.4"
          req="SM-CAPA-001"
        />
        <KPICard
          title="Change Controls (QA)"
          value={metrics.changeControlsPendingQA}
          icon={FileText}
          link="/change-control?status=pending-qa"
          color="#3b82f6"
          prd="PRD §6.5"
          req="SM-CC-001"
        />
        <KPICard
          title="Upcoming Self-Inspection"
          value={metrics.upcomingSelfInspections}
          unit="in 12 days"
          icon={CheckCircle2}
          link="/audits/self-inspections"
          color="#10b981"
          prd="PRD §6.6"
          req="SM-SI-001"
        />
        <KPICard
          title="Audit Readiness Index"
          value={metrics.auditReadinessIndex}
          unit="/100"
          trend="up"
          trendValue="+5 pts"
          icon={BarChart3}
          link="/audit-packs"
          color="#10b981"
          prd="PRD §6.13"
          req="SM-AUD-001"
        />
        <KPICard
          title="Training Compliance"
          value={metrics.trainingCompliance}
          unit="%"
          icon={Users}
          link="/documents?tab=training"
          color="#8b5cf6"
          prd="PRD §6.12"
          req="SM-TRN-001"
        />
        </div>
      </ReportSection>

      {/* Charts Row */}
      <ReportSection
        id="charts"
        title="Compliance Analytics"
        description="Trends and category breakdowns"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Compliance Trend */}
          <Card className={`bg-white border border-slate-200 col-span-2 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §11" req="SM-PQR-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Compliance Trend ({timeRange === '7days' ? 'Last 7 Days' : timeRange === '30days' ? 'Last 30 Days' : 'This Quarter'})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={complianceTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey={timeRange === '7days' ? 'day' : timeRange === '30days' ? 'week' : 'month'} stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} domain={[75, 100]} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                    labelStyle={{ color: '#0f172a' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#fd4816" 
                    strokeWidth={2}
                    dot={{ fill: '#fd4816', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Deviations by Category */}
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §6.4" req="SM-DEV-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Deviations by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={deviationsByCategory}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {deviationsByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {deviationsByCategory.map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx] }} />
                  <span className="text-xs text-slate-600">{item.name} ({item.value})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>
      </ReportSection>

      {/* At Risk Today & Demo Paths */}
      <ReportSection
        id="at-risk"
        title="At Risk Items & Demo Paths"
        description="Items requiring immediate attention and workflow demonstrations"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* At Risk Today */}
          <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §7.1" req="SM-QRM-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              At Risk Today
            </CardTitle>
            <CardDescription className="text-slate-600">Items requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {atRiskItems.map((item) => {
              const IconComponent = iconMap[item.icon] || AlertTriangle;
              return (
                <Link 
                  key={item.id} 
                  to={item.link}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-colors group"
                >
                  <IconComponent className={`h-4 w-4 shrink-0 ${
                    item.severity === 'Critical' ? 'text-red-500' :
                    item.severity === 'High' || item.severity === 'Major' ? 'text-amber-500' :
                    'text-blue-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate font-medium">{item.title}</p>
                    <p className="text-xs text-slate-600">{item.type}</p>
                  </div>
                  <Badge className={`text-xs shrink-0 ${
                    item.severity === 'Critical' ? 'bg-red-50 text-red-700 border-red-200' :
                    item.severity === 'High' || item.severity === 'Major' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-blue-50 text-blue-700 border-blue-200'
                  }`}>
                    {item.severity}
                  </Badge>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                </Link>
              );
            })}
          </CardContent>
        </Card>

        {/* Demo Paths */}
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §7.1" req="SM-PQS-001" />
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-800">Demo Paths</CardTitle>
            <CardDescription className="text-slate-600">Explore end-to-end compliance workflows</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {demoPaths.map((demo, idx) => (
              <Link 
                key={idx} 
                to={demo.path}
                className="block p-3 rounded-lg bg-white border border-slate-200 hover:border-[#fd4816]/50 hover:shadow-sm transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-800 group-hover:text-[#fd4816] transition-colors">
                    {demo.title}
                  </p>
                  <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-[#fd4816] transition-colors" />
                </div>
                <p className="text-xs text-slate-600 mt-1">{demo.description}</p>
              </Link>
            ))}
          </CardContent>
        </Card>
        </div>
      </ReportSection>

      {/* Business Metrics */}
      <ReportSection
        id="business-metrics"
        title="Business Metrics"
        description="Financial and operational metrics"
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §3" req="SM-PQS-001" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Remediation Budget (FY26)</p>
            <p className="text-xl font-bold text-slate-900">{formatIndianCurrency(metrics.remediationBudget)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §3" req="SM-TRN-001" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Training Spend (Q1)</p>
            <p className="text-xl font-bold text-slate-900">{formatIndianCurrency(metrics.trainingSpendQ1)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §6.4" req="SM-DEV-001" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Avg Deviation Closure Cost</p>
            <p className="text-xl font-bold text-slate-900">{formatIndianCurrency(metrics.avgDeviationClosureCost)}</p>
          </CardContent>
        </Card>
        <Card className={`bg-white border border-slate-200 relative shadow-sm ${pidTraceMode ? 'border-cyan-300' : ''}`}>
          <TraceLabel prd="PRD §6.13" req="SM-AUD-001" />
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Audit Risk Exposure</p>
            <p className="text-xl font-bold text-red-600">{formatIndianCurrency(metrics.potentialAuditRiskExposure)}</p>
          </CardContent>
        </Card>
        </div>
      </ReportSection>

      {/* Footer */}
      <div className="text-center py-4 border-t border-slate-200">
        <p className="text-xs text-slate-500">
          This system supports Schedule-M compliance evidence collection and audit readiness.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
